import type { Dispatch, SetStateAction } from "react";

import { apiClient } from "~/services/apiClient";
import type { IncomingCallInfo } from "~/services/calling/types";
import { applyIncomingCall } from "~/context/call/callState";
import type { CallState } from "~/context/call/callState";
import { getIncomingCallUniqueId } from "~/context/call/helpers";

export type ActiveCallApiRecord = {
  id?: unknown;
  asteriskId?: unknown;
  callUuid?: unknown;
  callId?: unknown;
  uniqueId?: unknown;
  callerNumber?: unknown;
  caller?: unknown;
  customerId?: unknown;
  customerName?: unknown;
  status?: unknown;
  startedAt?: unknown;
  endedAt?: unknown;
  direction?: unknown;
  deviceType?: unknown;
  callReference?: unknown;
  trunkNumber?: unknown;
  trunk?: unknown;
};

const TERMINAL_QUEUE_STATUS_TOKENS = new Set([
  "ended",
  "completed",
  "complete",
  "answered",
  "missed",
  "abandoned",
  "abandon",
  "hangup",
  "hungup",
  "closed",
  "cancelled",
  "canceled",
  "rejected",
  "declined",
  "noanswer",
  "timeout",
  "timedout",
  "unanswered",
  "dropped",
]);

function asString(value: unknown): string | undefined {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return undefined;
}

function normalizeStatusToken(value: unknown) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function parseDateMs(value: unknown) {
  const raw = asString(value);
  if (!raw) return null;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

function isActiveQueueCall(record: ActiveCallApiRecord) {
  if (parseDateMs(record.endedAt)) return false;
  const statusToken = normalizeStatusToken(record.status);
  if (statusToken && TERMINAL_QUEUE_STATUS_TOKENS.has(statusToken)) return false;
  return true;
}

export function mapActiveCallApiRecord(
  record: ActiveCallApiRecord
): IncomingCallInfo | null {
  if (!isActiveQueueCall(record)) return null;

  const uniqueId =
    asString(record.uniqueId) ??
    asString(record.asteriskId) ??
    asString(record.callUuid) ??
    asString(record.callId) ??
    asString(record.id);
  const callId =
    asString(record.callId) ??
    asString(record.id) ??
    (uniqueId && uniqueId !== asString(record.asteriskId) ? uniqueId : undefined);
  const callerNumber =
    asString(record.callerNumber) ?? asString(record.caller) ?? null;
  const receivedAtMs = parseDateMs(record.startedAt) ?? Date.now();
  const service =
    asString(record.deviceType) ??
    asString(record.callReference) ??
    asString(record.trunkNumber) ??
    asString(record.trunk) ??
    null;

  if (!uniqueId && !callId && !callerNumber) return null;

  return {
    id: uniqueId ?? callId ?? `call-${receivedAtMs}`,
    uniqueId: uniqueId ?? null,
    customerId: asString(record.customerId) ?? null,
    callUuid: uniqueId ?? null,
    callId: callId ?? null,
    callerNumber,
    customerName: asString(record.customerName) ?? null,
    service,
    description: "Voice Call",
    signalType: null,
    requiresAnswer: null,
    metadata: null,
    receivedAtMs,
  };
}

function buildAuthoritativeIdentitySet(calls: IncomingCallInfo[]) {
  const ids = new Set<string>();
  for (const call of calls) {
    const uniqueId = getIncomingCallUniqueId(call);
    if (uniqueId) ids.add(uniqueId);
    const callId = call.callId?.trim();
    if (callId) ids.add(callId);
  }
  return ids;
}

export function reconcileIncomingCallsWithAuthoritative(
  prev: CallState,
  authoritativeCalls: IncomingCallInfo[]
): CallState {
  const authoritativeIds = buildAuthoritativeIdentitySet(authoritativeCalls);

  const prunedIncoming = prev.incomingCalls.filter((call) => {
    const uniqueId = getIncomingCallUniqueId(call);
    const callId = call.callId?.trim();
    if (!uniqueId && !callId) return true;
    if (uniqueId && authoritativeIds.has(uniqueId)) return true;
    if (callId && authoritativeIds.has(callId)) return true;
    return false;
  });

  let next: CallState = { ...prev, incomingCalls: prunedIncoming };
  for (const call of authoritativeCalls) {
    next = applyIncomingCall(next, call);
  }

  const hasIncoming = next.incomingCalls.length > 0;
  if (
    !hasIncoming &&
    next.callDirection === "incoming" &&
    next.callStatus === "ringing"
  ) {
    return {
      ...next,
      callStatus: "idle",
      callDirection: undefined,
    };
  }

  return next;
}

export async function fetchActiveIncomingCalls(limit = 200) {
  const response = await apiClient.get<ActiveCallApiRecord[]>("/api/calls", {
    params: { limit },
  });
  const records = Array.isArray(response.data) ? response.data : [];
  return records
    .map(mapActiveCallApiRecord)
    .filter((entry): entry is IncomingCallInfo => Boolean(entry));
}

export async function resyncActiveIncomingCalls(
  setState: Dispatch<SetStateAction<CallState>>
) {
  const authoritativeCalls = await fetchActiveIncomingCalls();
  setState((prev) =>
    reconcileIncomingCallsWithAuthoritative(prev, authoritativeCalls)
  );
}
